import React from 'react';
import { cn } from '@express-labs/raven-tools';
import {
  CarouselProvider,
  Slider,
  ButtonBack,
  ButtonNext,
} from 'pure-react-carousel';
import { SLIDE_HEIGHT, SLIDE_WIDTH } from '../../shared';
import IconLeft from '../IconLeft';
import IconRight from '../IconRight';
import s from '../../Carousel.scss';
import styles from './CarouselCategories.scss';
import RenderSlideCategories from '../RenderSlideCategories';
import { CarouselCards } from '../../Carousel';

export type CarouselCategoriesProps = {
  /** an array of object shape, this value is currently used for Category Carousel */
  cards?: CarouselCards[];
  /** Moves the nth slide into the leftmost position of the carousel */
  currentSlide?: number;
  /** defines the device width. string: desktop or mobile */
  deviceWidth?: number;
  mainImages?: never;
};

export type CarouselCategoriesPropsInjectedByCarousel = {
  onClick: (i: string, ev: React.MouseEvent<HTMLElement>) => void;
  /**  a unique id for the carousel. This number is used to help create unique css id's and class
  names so that you can have more than one carousel on the page and style them differently,
  or reference a dom element via their unique id attribute name via javascript.
  This number is generated automatically in Carousel and passed in. */
  uid: number;
};

// Max limit visible slides in Carousel by device-width
const MAX_VISIBLE_SLIDES_DESKTOP = 5;
const MAX_VISIBLE_SLIDES_MOBILE = 2;

const ColumnCardSize = {
  single: 1,
  double: 2,
  triple: 3,
};

export function getTotalSlides(cards: CarouselCards[]) {
  return cards.reduce((total, currentImage) => {
    const cardSize =
      currentImage.type && ColumnCardSize[currentImage.type]
        ? ColumnCardSize[currentImage.type]
        : ColumnCardSize.single;
    return cardSize + total;
  }, 0);
}

export function getSlidesMainImages(cards: CarouselCards[]) {
  const singleSlideSize = 100 / getTotalSlides(cards);

  return cards.map((card) => ({
    ...card,
    slideSize:
      card.type && ColumnCardSize[card.type]
        ? ColumnCardSize[card.type] * singleSlideSize
        : singleSlideSize,
  }));
}

const CarouselCategories = ({
  cards = [],
  currentSlide = 0,
  deviceWidth = 0,
  onClick,
  uid,
}: CarouselCategoriesProps & CarouselCategoriesPropsInjectedByCarousel) => {
  const isDesktopDevice = deviceWidth > 768;
  const MAX_VISIBLE_SLIDES = isDesktopDevice
    ? MAX_VISIBLE_SLIDES_DESKTOP
    : MAX_VISIBLE_SLIDES_MOBILE;

  const totalSlides = getTotalSlides(cards);
  const isLimitSlidesExceeded = totalSlides > MAX_VISIBLE_SLIDES;
  const visibleSlides = isLimitSlidesExceeded
    ? MAX_VISIBLE_SLIDES + 0.33
    : totalSlides;
  const sliderClassName = isDesktopDevice
    ? styles.sliderTrayDesktop
    : styles.sliderTrayMobile;
  return (
    <div className={styles.centeredContainerWrapper}>
      <CarouselProvider
        className={cn(
          s.container,
          styles.centeredContainer,
          'carousel--centered',
          'carousel--categories'
        )}
        currentSlide={currentSlide}
        data-testid="CarouselCategories"
        data-uid={uid}
        lockOnWindowScroll
        naturalSlideHeight={SLIDE_HEIGHT}
        naturalSlideWidth={SLIDE_WIDTH}
        totalSlides={totalSlides}
        visibleSlides={visibleSlides}
      >
        <div className={styles.sliderButtonContainer}>
          <Slider
            className={cn(styles.centeredSlider, styles.carouselSlider)}
            classNameTray={sliderClassName}
            trayTag="div"
          >
            {getSlidesMainImages(cards).map((card, i) => (
              <RenderSlideCategories
                categoryCardId={card.id}
                ctaText={card.ctaText}
                deviceWidth={deviceWidth}
                i={i}
                isLazyLoaded={i > visibleSlides - 1}
                key={card.id}
                main={card.imagePath}
                onClick={onClick}
                size={card.slideSize}
                totalSlides={cards.length}
                uid={uid}
              />
            ))}
          </Slider>
          {isLimitSlidesExceeded && isDesktopDevice && (
            <>
              <ButtonBack className={styles.back}>
                <IconLeft />
              </ButtonBack>
              <ButtonNext className={styles.next}>
                <IconRight />
              </ButtonNext>
            </>
          )}
        </div>
      </CarouselProvider>
    </div>
  );
};

export default CarouselCategories;
